import { ContactFieldAddressInput } from '@/api/address-book/types';
import type { RankedCandidate } from '@/api/contact-import/types';

import { SocialProfileKey } from './components/social-profiles-block';

export type EntryMethod = 'qr' | 'card' | 'manual';
export type ContactType = 'individual' | 'organization';

export interface SocialLink {
  platform: SocialProfileKey;
  value: string;
}

export interface ContactData {
  contactType?: ContactType;
  name?: string;
  emails?: string[];
  phones?: string[];
  jobTitle?: string;
  organizationId?: string;
  organizationName?: string;
  addresses?: ContactFieldAddressInput[];
  websites?: string[];
  notes?: string;
  socialLinks?: SocialLink[];
  iconId?: string;
}

export type Step =
  | 'methodSelect'
  | 'qrScan'
  | 'cardUpload'
  | 'reviewCard'
  | 'matchCheck'
  | 'merging'
  | 'contactForm'
  | 'orgCreate'
  | 'categorySelect'
  | 'reviewSave'
  | 'creating'
  | 'success'
  | 'closed';

export type FlowEvent =
  | { type: 'PICK_QR' }
  | { type: 'PICK_CARD' }
  | { type: 'PICK_MANUAL' }
  | {
      type: 'SCANNED';
      data: Partial<ContactData>;
      referenceId: string;
      contactCode: string;
      chooseCategories?: boolean;
    }
  | { type: 'CODE_CONSUMED' }
  | { type: 'EXTRACT_OK'; data: Partial<ContactData> }
  | {
      type: 'REVIEW_CONTINUE';
      data: Partial<ContactData>;
      candidates: RankedCandidate[];
      skipCategories: boolean;
    }
  | { type: 'MATCH_RESOLVED'; contactId: string | null; skipCategories: boolean }
  | { type: 'MERGE_OK' }
  | { type: 'MERGE_FAIL' }
  | { type: 'SUBMIT'; data: Partial<ContactData> }
  | { type: 'SUBMIT_DIRECT'; data: Partial<ContactData> }
  | { type: 'FINISH_CATEGORIES'; ids: string[] }
  | { type: 'SKIP_CATEGORIES' }
  | { type: 'SAVE_REVIEWED'; notes: string }
  | { type: 'CREATE_ORG'; name: string; formSnapshot: Partial<ContactData> }
  | { type: 'ORG_CREATED'; orgId: string; orgName: string }
  | { type: 'CREATE_OK'; contactId: string; isNew: boolean }
  | { type: 'CREATE_FAIL' }
  | { type: 'EDIT_CATEGORIES' }
  | { type: 'FINISH' }
  | { type: 'BACK' }
  | { type: 'PRESET_CATEGORIES'; ids: string[] }
  | { type: 'RESET'; startWithManualEntry?: boolean };

export interface FlowState {
  step: Step;
  method: EntryMethod | null;
  data: Partial<ContactData>;
  categoryIds: string[];
  presetCategoryIds: string[];
  pendingCode?: string;
  qrReferenceId?: string;
  qrContactCode?: string;
  createdContactId?: string;
  createdContactIsNew?: boolean;
  orgCreateName?: string;
  matchCandidates?: RankedCandidate[];
  mergeContactId?: string;
  skippedCategories?: boolean;
}

export const createInitialState = (
  presetCategoryIds: string[] = [],
  startWithManualEntry = false,
): FlowState => ({
  step: startWithManualEntry ? 'contactForm' : 'methodSelect',
  method: startWithManualEntry ? 'manual' : null,
  data: {},
  categoryIds: presetCategoryIds,
  presetCategoryIds,
});

export const initialState: FlowState = createInitialState();
